import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { pressPoint } from "./glass";
import renderIcon from "./renderIcon";

const cx = (...a) => a.filter(Boolean).join(" ");

// săgețile sus-jos, ca la meniurile de alegere de pe iOS
function Chevrons() {
   return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
         <path
            d="M8 9.5 12 5.5l4 4M8 14.5l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
         />
      </svg>
   );
}

/**
 * DropdownUI — meniul derulant al aplicației (alegerea unei valori dintr-o listă).
 * Se deschide ca pe iOS: meniul plutitor stă fix peste buton și apare cu o scalare
 * elastică mică; butonul se stinge dedesubt, ca fundalul să nu apară dublat.
 * Liquid Glass doar pe buton și pe meniu; opțiunile n-au fundal, doar cea aleasă
 * are unul ușor transparent. Stilurile: scss/UI/_dropdownUI.scss.
 *
 * options: [{ value, label, icon?, disabled?, checked? }] · value · onChange(valoare)
 *    { heading: "Sector" } = titlul unei secțiuni, { divider: true } = linia dintre secțiuni
 *    (nu se aleg, tastatura le sare) · checked: bifată, în meniul de acțiuni (fără value)
 * placeholder: textul fără valoare aleasă („Alege”) · label: textul butonului, în
 *    locul etichetei opțiunii alese (ex. „120%” la zoom; "" = doar iconița)
 * icon: iconița butonului (nume UIIcon / svg / element), dacă opțiunea aleasă n-are
 * tone: primary = butonul în culoarea accentului — meniul de acțiuni („+ Adaugă”:
 *    fără value, fiecare opțiune face ceva în onChange); meniul rămâne neutru
 * size: md (46px, implicit, cât căutarea) | lg (52px, lângă ButtonUI lg)
 * surface: page = butonul și meniul pe --black-s, pe fundalul paginii (implicit
 *    --black-p, pe un panou --black-s)
 * block: pe toată lățimea · align: start (implicit) | end — meniul lipit la dreapta
 * chevrons: false = fără săgețile din buton (butonul doar cu iconiță, „⋯”)
 * aria-label: numele alegerii (butonul îl citește cu valoarea aleasă)
 * Tastatura: ↓ / ↑ / Enter / Spațiu deschid; în meniu ↑ ↓ Home End mută, Enter
 *    alege, Escape închide și întoarce focusul pe buton, Tab iese.
 */
export default function DropdownUI({
   options = [],
   value,
   onChange,
   placeholder = "Alege",
   label: shownLabel,
   icon,
   tone,
   size = "md",
   surface,
   block = false,
   align = "start",
   chevrons = true,
   disabled = false,
   className = "",
   style,
   "aria-label": ariaLabel,
}) {
   const [open, setOpen] = useState(false);
   const rootRef = useRef(null);
   const triggerRef = useRef(null);
   const menuRef = useRef(null);
   const menuId = `dropdownUI-${useId()}`;

   const isChoice = (o) => !o.heading && !o.divider;
   const current = options.find((o) => isChoice(o) && String(o.value) === String(value));
   const text = shownLabel ?? current?.label ?? placeholder;

   const close = useCallback((returnFocus) => {
      setOpen(false);
      if (returnFocus) triggerRef.current?.focus();
   }, []);

   useEffect(() => {
      if (!open) return undefined;
      // focusul pe opțiunea aleasă (sau pe prima), ca săgețile să meargă de acolo
      const items = menuRef.current?.querySelectorAll('[role="option"]:not(:disabled)');
      const selected = menuRef.current?.querySelector('[aria-selected="true"]:not(:disabled)');
      (selected || items?.[0])?.focus({ preventScroll: true });

      const onOutside = (e) => {
         if (!rootRef.current?.contains(e.target)) setOpen(false);
      };
      const onKey = (e) => {
         if (e.key === "Escape") {
            e.preventDefault();
            close(true);
         }
      };
      document.addEventListener("pointerdown", onOutside, true);
      document.addEventListener("keydown", onKey);
      return () => {
         document.removeEventListener("pointerdown", onOutside, true);
         document.removeEventListener("keydown", onKey);
      };
   }, [open, close]);

   const pick = (o) => {
      if (o.disabled) return;
      onChange?.(o.value);
      close(true);
   };

   const onTriggerKey = (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
         e.preventDefault();
         setOpen(true);
      }
   };

   const onMenuKey = (e) => {
      const items = [...menuRef.current.querySelectorAll('[role="option"]:not(:disabled)')];
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      let next = null;
      if (e.key === "ArrowDown") next = items[(i + 1) % items.length];
      else if (e.key === "ArrowUp") next = items[(i - 1 + items.length) % items.length];
      else if (e.key === "Home") next = items[0];
      else if (e.key === "End") next = items[items.length - 1];
      else if (e.key === "Tab") setOpen(false);
      if (next) {
         e.preventDefault();
         next.focus();
      }
   };

   return (
      <div
         ref={rootRef}
         className={cx(
            "dropdownUI",
            size !== "md" && `dropdownUI--${size}`,
            text === "" && !chevrons && "dropdownUI--iconOnly",
            tone === "primary" && "dropdownUI--primary",
            surface === "page" && "dropdownUI--page",
            block && "dropdownUI--block",
            align === "end" && "dropdownUI--end",
            open && "is-open",
            className,
         )}
         style={style}
      >
         <button
            ref={triggerRef}
            type="button"
            className="dropdownUI__trigger"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={menuId}
            aria-label={ariaLabel && typeof text === "string" ? `${ariaLabel}: ${text}` : ariaLabel}
            disabled={disabled}
            onPointerDown={pressPoint}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={onTriggerKey}
         >
            {(current?.icon || icon) && renderIcon(current?.icon || icon, "dropdownUI__icon")}
            {text !== "" && <span className="dropdownUI__label">{text}</span>}
            {chevrons ? (
               <span className="dropdownUI__chevrons">
                  <Chevrons />
               </span>
            ) : null}
         </button>

         {/* rămâne în pagină, ascuns, ca să se poată anima și la închidere */}
         <div
            ref={menuRef}
            id={menuId}
            className="dropdownUI__menu"
            role="listbox"
            aria-label={ariaLabel}
            onKeyDown={onMenuKey}
         >
            {options.map((o, i) => {
               if (o.heading) {
                  return (
                     <div key={`h${i}`} className="dropdownUI__heading" role="presentation">
                        {o.heading}
                     </div>
                  );
               }
               if (o.divider) return <div key={`d${i}`} className="dropdownUI__divider" role="separator" />;
               const on = Boolean(o.checked) || (value !== undefined && String(o.value) === String(value));
               return (
                  <button
                     key={String(o.value)}
                     type="button"
                     role="option"
                     aria-selected={on}
                     disabled={o.disabled}
                     className={cx("dropdownUI__option", on && "is-selected")}
                     onClick={() => pick(o)}
                  >
                     {o.icon && renderIcon(o.icon, "dropdownUI__icon")}
                     <span className="dropdownUI__text">{o.label}</span>
                  </button>
               );
            })}
         </div>
      </div>
   );
}
